import type { Metric, Dimension, FactTable } from 'AnalyticsDashboard/utils/factTableHelpers'

declare const _: any

// Dimensions that have entity lookups in dimensionInfo (id -> name)
export const ENTITY_DIMENSIONS: Record<string, { source: string; label: string }> = {
  campaign_id: { source: 'campaignId.name', label: 'Campaign' },
  flow_id: { source: 'flowId.name', label: 'Flow' },
  client_id: { source: 'clientId.name', label: 'Client' },
}

export const TIME_BUCKETS = ['hour', 'day', 'week', 'month', 'quarter', 'year']

interface SlotDef {
  label: string
  required: boolean
  kind: 'metric' | 'dimension' | 'time' | 'geoDimension' | 'boolean' | 'html'
  single?: boolean
  includeTime?: boolean
}

interface SlotValues {
  [key: string]: any
}

export interface ChartTypeDef {
  label: string
  description: string
  static?: boolean
  supportsOrderLimit: boolean
  slots: Record<string, SlotDef>
  defaults: () => SlotValues
  collectDimensions: (slotValues: SlotValues) => Array<{ id: string; timeBucket?: string }>
  buildChartOptions: (slotValues: SlotValues, metrics: Metric[], availableDimensions?: Dimension[]) => Record<string, any>
}

const DEFAULT_TIME_DIM = { id: 'time', timeBucket: 'day' }

const barColumnBase: Omit<ChartTypeDef, 'label' | 'description'> = {
  supportsOrderLimit: true,
  slots: {
    metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
    xAxis: { label: 'X axis', required: true, kind: 'dimension', single: true, includeTime: true },
    segmentBy: { label: 'Segment by', required: false, kind: 'dimension', single: true },
    stacked: { label: 'Stacked', required: false, kind: 'boolean' },
  },
  defaults: () => ({
    xAxis: DEFAULT_TIME_DIM,
    metrics: [],
    segmentBy: null,
    stacked: false,
  }),
  collectDimensions: (slotValues) => {
    const dims: Array<{ id: string; timeBucket?: string }> = []
    if (slotValues.xAxis) dims.push(slotValues.xAxis)
    if (slotValues.segmentBy) dims.push(slotValues.segmentBy)
    return dims
  },
  buildChartOptions: (slotValues, metrics) => {
    const opts: Record<string, any> = {}
    opts.x = slotValues.xAxis?.id || 'time'
    opts.y = metrics.length === 1
      ? metrics[0].id
      : metrics.map(m => m.id)
    if (slotValues.segmentBy) opts.seriesBy = _.camelCase(slotValues.segmentBy.id)
    if (slotValues.stacked) opts.stacked = true
    return opts
  },
}

export const CHART_TYPES: Record<string, ChartTypeDef> = {
  lineV2: {
    label: 'Line Chart',
    description: 'Track metrics over time',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
      timeDimension: { label: 'Time dimension', required: true, kind: 'time', single: true },
      segmentBy: { label: 'Segment by', required: false, kind: 'dimension', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: () => ({
      timeDimension: DEFAULT_TIME_DIM,
      metrics: [],
      segmentBy: null,
      comparePrevious: false,
    }),
    collectDimensions: (slotValues) => {
      const dims: Array<{ id: string; timeBucket?: string }> = []
      if (slotValues.timeDimension) dims.push(slotValues.timeDimension)
      if (slotValues.segmentBy) dims.push(slotValues.segmentBy)
      return dims
    },
    buildChartOptions: (slotValues, metrics) => {
      const opts: Record<string, any> = {}
      opts.x = slotValues.timeDimension?.id || 'time'
      opts.y = metrics.length === 1
        ? metrics[0].id
        : metrics.map(m => m.id)
      if (slotValues.segmentBy) {
        opts.seriesBy = _.camelCase(slotValues.segmentBy.id)
      }
      if (slotValues.comparePrevious) opts.comparePrevious = true
      return opts
    },
  },
  columnV2: {
    ...barColumnBase,
    label: 'Column Chart',
    description: 'Compare values across categories or time',
  } as ChartTypeDef,
  barV2: {
    ...barColumnBase,
    label: 'Bar Chart',
    description: 'Horizontal bars for category comparison',
    buildChartOptions: (slotValues, metrics) => ({
      ...barColumnBase.buildChartOptions(slotValues, metrics),
      orientation: 'h',
    }),
  } as ChartTypeDef,
  pieV2: {
    label: 'Pie Chart',
    description: 'Show proportions of a whole',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      sliceBy: { label: 'Slice by', required: true, kind: 'dimension', single: true },
      donut: { label: 'Donut style', required: false, kind: 'boolean' },
    },
    defaults: () => ({
      sliceBy: null,
      metrics: [],
      donut: false,
    }),
    collectDimensions: (slotValues) => {
      return slotValues.sliceBy ? [slotValues.sliceBy] : []
    },
    buildChartOptions: (slotValues, metrics) => ({
      labels: _.camelCase(slotValues.sliceBy?.id || ''),
      values: metrics[0]?.id || '',
      donut: !!slotValues.donut,
    }),
  },
  table: {
    label: 'Table',
    description: 'Rows grouped by dimensions',
    supportsOrderLimit: true,
    slots: {
      metrics: { label: 'Metrics', required: true, kind: 'metric', single: false },
      groupBy: { label: 'Group by', required: false, kind: 'dimension', single: false },
    },
    defaults: () => ({
      metrics: [],
      groupBy: [],
    }),
    collectDimensions: (slotValues) => {
      if (Array.isArray(slotValues.groupBy)) return [...slotValues.groupBy]
      return []
    },
    buildChartOptions: () => ({}),
  },
  number: {
    label: 'Number',
    description: 'Single metric value',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: () => ({
      metrics: [],
      comparePrevious: false,
    }),
    collectDimensions: () => [],
    buildChartOptions: (slotValues) => {
      const opts: Record<string, any> = {}
      if (slotValues.comparePrevious) opts.comparePrevious = true
      return opts
    },
  },
  gauge: {
    label: 'Gauge',
    description: 'Metric with range indicator',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
    },
    defaults: () => ({
      metrics: [],
    }),
    collectDimensions: () => [],
    buildChartOptions: () => ({}),
  },
  map: {
    label: 'Map',
    description: 'Choropleth map by geographic area',
    supportsOrderLimit: false,
    slots: {
      metrics: { label: 'Metric', required: true, kind: 'metric', single: true },
      geoDimension: { label: 'Geographic dimension', required: true, kind: 'geoDimension', single: true },
    },
    defaults: () => ({
      metrics: [],
      geoDimension: null,
    }),
    collectDimensions: (slotValues) => {
      return slotValues.geoDimension ? [slotValues.geoDimension] : []
    },
    buildChartOptions: (slotValues, metrics, availableDimensions) => {
      const dimId = slotValues.geoDimension?.id
      const dimInfo = availableDimensions?.find(d => d.id === dimId)
      return {
        mapId: dimInfo?.geo || 'world',
        values: metrics[0]?.id || '',
      }
    },
  },
  pivotTable: {
    label: 'Pivot Table',
    description: 'Heatmap grid with two dimensions',
    supportsOrderLimit: true,
    slots: {
      metrics:         { label: 'Metric', required: true, kind: 'metric', single: true },
      rowDimension:    { label: 'Row dimension', required: true, kind: 'dimension', single: true },
      columnDimension: { label: 'Column dimension', required: false, kind: 'dimension', single: true },
      timeDimension:   { label: 'Time dimension', required: false, kind: 'time', single: true },
      comparePrevious: { label: 'Compare with previous period', required: false, kind: 'boolean' },
    },
    defaults: () => ({
      metrics: [],
      rowDimension: null,
      columnDimension: null,
      timeDimension: DEFAULT_TIME_DIM,
      comparePrevious: false,
    }),
    collectDimensions: (slotValues) => {
      const dims: Array<{ id: string; timeBucket?: string }> = []
      if (slotValues.rowDimension) dims.push(slotValues.rowDimension)
      if (slotValues.columnDimension) dims.push(slotValues.columnDimension)
      if (slotValues.timeDimension) dims.push(slotValues.timeDimension)
      return dims
    },
    buildChartOptions: (slotValues, metrics) => ({
      rowDimension: _.camelCase(slotValues.rowDimension?.id || ''),
      ...(slotValues.columnDimension ? { columnDimension: _.camelCase(slotValues.columnDimension.id) } : {}),
      timeDimension: slotValues.timeDimension ? _.camelCase(slotValues.timeDimension.id) : null,
      metric: metrics[0]?.id || '',
      ...(slotValues.comparePrevious ? { comparePrevious: true } : {}),
    }),
  },
  htmlBlock: {
    label: 'HTML Block',
    description: 'Rich text content section',
    static: true,
    supportsOrderLimit: false,
    slots: {
      html: { label: 'Content', required: false, kind: 'html' },
    },
    defaults: () => ({
      html: '',
    }),
    collectDimensions: () => [],
    buildChartOptions: (slotValues) => ({
      html: slotValues.html || '',
    }),
  },
}
